import React from 'react';
import { Link } from 'react-router-dom';
import './rush.css';
import './legal.css';
import './security.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { LegalPage } from './LegalPage';

const TRUST_BADGES = [
  { label: 'SOC 2', sub: 'Type II', tone: 'ok' },
  { label: 'ISO 27001', sub: 'In progress', tone: 'warn' },
  { label: 'GDPR', sub: 'Compliant', tone: 'ok' },
  { label: 'HIPAA', sub: 'BAA available', tone: 'ok' },
  { label: 'Pen-tested', sub: 'Annually', tone: 'ok' },
  { label: 'EU Hosted', sub: 'Frankfurt · Dublin', tone: 'ok' },
];

const PILLARS = [
  { k: '01', title: 'Encrypted end-to-end', body: 'TLS 1.3 in transit on every request. AES-256-GCM at rest for every byte we store — including backups, snapshots, and translation memory.' },
  { k: '02', title: 'Hosted on AWS in the EU', body: 'Primary in Frankfurt (eu-central-1), replicated to Dublin (eu-west-1). Your data does not cross the Atlantic for storage.' },
  { k: '03', title: 'Mandatory 2FA', body: 'TOTP, WebAuthn, and passkeys for every account. SSO with SAML / OIDC and SCIM provisioning on Scale plans.' },
  { k: '04', title: 'Audited and reviewed', body: 'SOC 2 Type II in good standing, annual third-party penetration tests, quarterly internal red-team exercises.' },
];

const INFRA_ROWS = [
  ['Compute', 'AWS ECS Fargate, isolated per-tenant task definitions', 'eu-central-1'],
  ['Application DB', 'Aurora PostgreSQL Serverless v2, encrypted with KMS', 'eu-central-1 (multi-AZ)'],
  ['Object storage', 'S3 with SSE-KMS, versioning, Object Lock for backups', 'eu-central-1 + eu-west-1'],
  ['Search index', 'OpenSearch on private subnets, no public ingress', 'eu-central-1'],
  ['Secrets', 'AWS Secrets Manager + KMS, rotated quarterly', 'eu-central-1'],
  ['Edge / WAF', 'Cloudflare in front of all public traffic', 'Global'],
  ['Logging', 'Centralized in CloudWatch + S3, retained 13 months', 'eu-central-1'],
];

const MFA_METHODS = [
  { title: 'Passkeys', badge: 'Recommended', tone: 'good', body: 'WebAuthn-backed passkeys sync across devices via iCloud Keychain, Google Password Manager, or 1Password. Phishing-resistant by design.' },
  { title: 'Hardware keys', badge: 'FIDO2', tone: 'good', body: 'YubiKey, Titan, or any FIDO2 / U2F-compatible authenticator. Required on Scale-plan admin accounts.' },
  { title: 'Authenticator apps', badge: 'TOTP', tone: 'ok', body: '30-second TOTP codes via 1Password, Authy, Google Authenticator, or any RFC 6238 client.' },
  { title: 'SMS', badge: 'Fallback only', tone: 'warn', body: "Available as a backup method. We don't recommend it as a primary — SMS is vulnerable to SIM-swap attacks." },
];

const SECTIONS = [
  { id: 'overview', title: 'Overview', body: (
    <>
      <p>Translation work means files leave your systems and live, briefly, in ours. We take that seriously. This page is the technical complement to our <Link to="/privacy">privacy policy</Link> and <Link to="/gdpr">DPA</Link> — what we actually do at the bytes-and-buildings level to keep your content safe.</p>
      <p>If something here is missing or you need it under NDA (pen-test reports, SOC 2 attestation letter, network diagrams), email <a href="mailto:legal@rush-studio.com">legal@rush-studio.com</a>.</p>
      <div className="legal-callout"><strong>Two-line summary:</strong> Rush Studio runs on AWS in EU regions, encrypts everything in transit and at rest, requires multi-factor authentication on every account, and is SOC 2 Type II certified with annual third-party penetration tests.</div>
    </>
  ) },
  { id: 'infrastructure', title: 'Infrastructure & hosting', body: (
    <>
      <p>Rush Studio is hosted exclusively on <strong>Amazon Web Services (AWS)</strong>. Production runs in <strong>Frankfurt (eu-central-1)</strong> with replication and warm standby in <strong>Dublin (eu-west-1)</strong>. We do not operate production infrastructure in the United States.</p>
      <p>The application sits on container workloads behind a VPC with private subnets for all stateful services. Public ingress is restricted to our edge (Cloudflare) and AWS ALB, both with WAF rules and rate-limiting.</p>
      <div className="legal-table">
        <div className="legal-table-row head">
          <span>Layer</span><span>Implementation</span><span>Region</span>
        </div>
        {INFRA_ROWS.map(([layer, impl, region]) => (
          <div className="legal-table-row" key={layer}>
            <span><strong>{layer}</strong></span>
            <span>{impl}</span>
            <span className="legal-region">{region}</span>
          </div>
        ))}
      </div>
      <p>AWS is contractually a sub-processor and is certified under SOC 1/2/3, ISO 27001/27017/27018, PCI DSS, and the EU-US Data Privacy Framework. See the <Link to="/gdpr">full sub-processor list</Link>.</p>
    </>
  ) },
  { id: 'encryption', title: 'Encryption', body: (
    <>
      <p><strong>In transit.</strong> Every connection to Rush Studio — web, API, integrations, internal service-to-service — is TLS 1.3 with modern ciphers. TLS 1.0 / 1.1 are disabled at the edge. HSTS is preloaded with <code>max-age=63072000; includeSubDomains; preload</code>.</p>
      <p><strong>At rest.</strong> Every byte we persist is encrypted with AES-256-GCM via AWS KMS. That includes the application database, S3 object storage, search indices, backups, log archives, and translation-memory exports. Each tenant's content is logically separated and keyed against a per-environment customer master key.</p>
      <p><strong>Key management.</strong> Master keys live in AWS KMS with automatic annual rotation. Application-level secrets (API tokens, OAuth client secrets, integration credentials) are stored in AWS Secrets Manager, rotated quarterly, and never written to logs or version control.</p>
      <p><strong>Passwords.</strong> Never stored. We hash with bcrypt at cost factor 12; the hash is what's compared, the plaintext is discarded the moment authentication finishes.</p>
    </>
  ) },
  { id: 'authentication', title: 'Authentication & 2FA', body: (
    <>
      <p>We require multi-factor authentication. <strong>Every account.</strong> No exceptions for free, paid, or trial users. The first time you sign in, you'll be walked through enrolling a second factor before you can access any workspace.</p>
      <div className="mfa-grid">
        {MFA_METHODS.map((m) => (
          <div className={`mfa-card mfa-${m.tone}`} key={m.title}>
            <div className="mfa-card-head">
              <h4>{m.title}</h4>
              <span className="mfa-badge">{m.badge}</span>
            </div>
            <p>{m.body}</p>
          </div>
        ))}
      </div>
      <p><strong>Recovery.</strong> When you enroll a 2FA method, we issue ten one-time recovery codes. Store them somewhere safe — they're the only way back into the account if you lose your authenticator. Used codes are invalidated immediately; the pool can be regenerated at any time, which invalidates the previous batch.</p>
      <p><strong>Single sign-on.</strong> On the Scale plan, SAML 2.0 and OIDC SSO are available against any identity provider (Okta, Azure AD / Entra ID, Google Workspace, OneLogin, JumpCloud, Auth0). SCIM 2.0 user provisioning and de-provisioning are included.</p>
      <p><strong>Session policy.</strong> Sessions expire after 30 days of inactivity (configurable down to 8 hours on Scale). Admins can force a sign-out across all devices at any time.</p>
    </>
  ) },
  { id: 'access-controls', title: 'Access controls', body: (
    <>
      <p><strong>In the product.</strong> Workspaces use role-based access control with five built-in roles (Owner, Admin, Manager, Linguist, Reviewer) and granular per-project overrides. Every action that touches customer content writes to an audit log, exportable in JSON.</p>
      <p><strong>Inside Rush Studio.</strong> Production access is limited to the on-call engineers who need it (currently six people). Access requires:</p>
      <ul>
        <li>An active SSO session with hardware-key MFA.</li>
        <li>A just-in-time approval from a second engineer for any database read.</li>
        <li>A justification recorded in our access-log channel, public to all employees.</li>
      </ul>
      <p>We do not have a generic "admin account" that can read customer content. All production access is identifiable, time-bounded, and reviewed quarterly.</p>
    </>
  ) },
  { id: 'vulnerability-management', title: 'Vulnerability management', body: (
    <>
      <p>We follow a four-track program:</p>
      <ul>
        <li><b>Dependency scanning</b> — Snyk and GitHub Dependabot run on every pull request and nightly across all repositories. Critical CVEs are patched within 24 hours.</li>
        <li><b>Static analysis</b> — Semgrep rules tuned to our stack run in CI; high-severity findings block merge.</li>
        <li><b>Dynamic scanning</b> — Authenticated DAST runs weekly against a staging mirror of production.</li>
        <li><b>Third-party penetration test</b> — Annually, by an external CREST-accredited firm. The latest report is available under NDA via <a href="mailto:legal@rush-studio.com">legal@rush-studio.com</a>.</li>
      </ul>
      <p>Findings are triaged within one business day, severity-mapped to CVSS, and tracked in a public-to-employees backlog with SLAs (Critical: 24h, High: 7d, Medium: 30d, Low: 90d).</p>
    </>
  ) },
  { id: 'incident-response', title: 'Incident response', body: (
    <>
      <p>An on-call rotation is staffed 24/7 with a 15-minute response SLA for security pages. For confirmed incidents involving customer data, we notify affected customers <strong>within 72 hours</strong> of becoming aware — typically much sooner — per the commitments in our <Link to="/gdpr">DPA</Link>.</p>
      <p>The notification includes the nature of the incident, the data categories affected, the likely consequences, and the mitigation steps taken or planned. A post-incident review with a written timeline and corrective actions is published to affected customers within 14 days.</p>
      <p>Live operational events — degradations, outages, suspected incidents — are posted in real time at <a href="#">status.rush.studio</a>.</p>
    </>
  ) },
  { id: 'backups', title: 'Backups & disaster recovery', body: (
    <>
      <p><strong>Backups.</strong> The application database is continuously backed up to S3 via point-in-time recovery, retained for 30 days. Object storage uses versioning plus weekly snapshots retained for 90 days. All backups are encrypted with separate KMS keys and stored cross-region.</p>
      <p><strong>Targets.</strong> RPO (recovery point objective): <strong>5 minutes</strong>. RTO (recovery time objective): <strong>4 hours</strong>. We test against these quarterly with full restore drills into an isolated environment.</p>
      <p><strong>Disaster recovery drill cadence.</strong> Quarterly. The last drill (Q1 2026) restored the full production database into a clean account in 1h 47m, well inside RTO.</p>
    </>
  ) },
  { id: 'people', title: 'People & operations', body: (
    <>
      <p>Every employee:</p>
      <ul>
        <li>Passes a background check before start date (jurisdiction permitting).</li>
        <li>Signs a confidentiality agreement covering customer data on day one.</li>
        <li>Completes mandatory security training on hire and annually thereafter.</li>
        <li>Uses a company-managed laptop with full-disk encryption, MDM, and an EDR agent (CrowdStrike).</li>
        <li>Has access reviewed quarterly. Access is revoked within one hour of termination.</li>
      </ul>
    </>
  ) },
  { id: 'data-handling', title: 'Data handling', body: (
    <>
      <p><strong>Data minimization.</strong> We collect only what's needed to deliver the service — see the <Link to="/privacy">privacy policy</Link> for the breakdown.</p>
      <p><strong>AI translation routing.</strong> When a segment is sent to an AI sub-processor, we transmit only the segment text plus any configured glossary terms. Account metadata, filenames, and project context never leave our infrastructure. All AI sub-processors contractually agree not to train on your data and to delete content after the translation is returned.</p>
      <p><strong>Tenant isolation.</strong> Customer data is logically separated by tenant ID enforced at every layer (database, search, object storage). Bulk export, deletion, and audit log access all operate within the tenant boundary.</p>
      <p><strong>Deletion.</strong> When you delete a project or close your account, content is purged from primary storage within 24 hours and from all backups within 30 days. We send written confirmation when the deletion completes.</p>
    </>
  ) },
  { id: 'compliance', title: 'Compliance & certifications', body: (
    <>
      <p>Current and in-progress:</p>
      <ul>
        <li><b>SOC 2 Type II</b> — in good standing, audited annually by a Big Four firm. Report available under NDA.</li>
        <li><b>ISO 27001</b> — certification in progress; expected Q3 2026.</li>
        <li><b>GDPR & UK GDPR</b> — full compliance; see <Link to="/gdpr">GDPR &amp; DPA page</Link>.</li>
        <li><b>CCPA / CPRA</b> — covered under the same data-subject rights flow as GDPR.</li>
        <li><b>HIPAA</b> — a Business Associate Agreement (BAA) is available for healthcare customers on the Scale plan. Email <a href="mailto:legal@rush-studio.com">legal@rush-studio.com</a>.</li>
        <li><b>PCI DSS</b> — we never see card numbers; payments are tokenized through Stripe. PCI is out of scope by design.</li>
      </ul>
      <p>For procurement teams running a vendor security assessment: we maintain prefilled responses for SIG Lite, CAIQ v4, and the EU Cloud CoC. <a href="mailto:legal@rush-studio.com">Request the latest copy</a>.</p>
    </>
  ) },
  { id: 'responsible-disclosure', title: 'Responsible disclosure', body: (
    <>
      <p>We don't run a public bug bounty (yet), but we welcome reports from security researchers and reward meaningful findings.</p>
      <p><strong>Send reports to:</strong> <a href="mailto:legal@rush-studio.com">legal@rush-studio.com</a>, optionally PGP-encrypted to <code>0x4F7C 2A8B 3D1E 9F00</code>.</p>
      <p><strong>Our promise.</strong> If you report in good faith and don't try to access other customers' data, exfiltrate data, run DoS attacks, or use social engineering: we will not pursue legal action, we will acknowledge within one business day, and we will keep you updated until the issue is closed. Confirmed novel findings receive a reward of $250–$10,000 depending on severity and impact.</p>
    </>
  ) },
  { id: 'contact', title: 'Contact', body: (
    <>
      <p><strong>Security issues:</strong> <a href="mailto:legal@rush-studio.com">legal@rush-studio.com</a> — monitored 24/7, target ack within 4 business hours, max one business day.</p>
      <p><strong>Privacy / DPO:</strong> Pedro Almeida, <a href="mailto:privacy@rush-studio.com">privacy@rush-studio.com</a>.</p>
      <p><strong>Procurement & vendor assessments:</strong> <a href="mailto:legal@rush-studio.com">legal@rush-studio.com</a>.</p>
      <p><strong>Postal:</strong> Rush Studio Ltd., 12 Hatton Garden, London EC1N 8AT, United Kingdom.</p>
    </>
  ) },
];

const HeroExtra = () => (
  <div className="sec-hero-extra">
    <div className="sec-trust">
      {TRUST_BADGES.map((b) => (
        <div className={`sec-trust-badge tone-${b.tone}`} key={b.label}>
          <span className="sec-trust-label">{b.label}</span>
          <span className="sec-trust-sub">{b.sub}</span>
        </div>
      ))}
    </div>
    <div className="sec-pillars">
      {PILLARS.map((p) => (
        <div className="sec-pillar" key={p.k}>
          <div className="sec-pillar-k">{p.k}</div>
          <h3>{p.title}</h3>
          <p>{p.body}</p>
        </div>
      ))}
    </div>
  </div>
);

const RushSecurityPage = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav />
      <LegalPage
        eyebrow="Trust · Security"
        title="How we secure your data"
        updated="May 20, 2026"
        intro="Rush Studio is built for translation work, which means files leave your systems and live, briefly, in ours. This page is the technical complement to our privacy policy and DPA — what we actually do at the bytes-and-buildings level."
        sections={SECTIONS}
        heroExtra={<HeroExtra />}
        related={[
          { href: '/privacy', label: 'Privacy Policy' },
          { href: '/gdpr', label: 'GDPR & DPA' },
          { href: '/terms', label: 'Terms of Service' },
          { href: '/contact', label: 'Contact us' },
        ]}
      />
      <RushFooter />
    </div>
  );
};

export default RushSecurityPage;
